import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { upsertProductoEnKit, quitarProductoDeKit } from '../../lib/stockApi'

const TIER_LABEL = { essential: 'Essential', premium: 'Premium', comercial: 'Comercial' }

export default function AdminKit() {
  const { codigo } = useParams()
  const [kit, setKit] = useState(null)
  const [items, setItems] = useState([])
  const [productosDisp, setProductosDisp] = useState([])
  const [loading, setLoading] = useState(true)
  const [modal, setModal] = useState(null) // null | { mode, productoId?, cantidad }

  useEffect(() => { load() }, [codigo])

  async function load() {
    setLoading(true)
    const [k, kitItems, prods] = await Promise.all([
      supabase.from('kits').select('*').eq('codigo', codigo).single(),
      supabase
        .from('kit_items')
        .select('cantidad, es_regalo, producto:productos(id, sku, nombre, presentacion, marca, costo_unitario, iva_pct, stock_actual, deleted_at, grupo_equivalencia)')
        .eq('kit_codigo', codigo),
      supabase
        .from('productos')
        .select('id, sku, nombre, presentacion, marca, costo_unitario, stock_actual')
        .eq('activo', true).is('deleted_at', null)
        .order('sku')
    ])
    setKit(k.data)
    setItems((kitItems.data || []).filter(i => i.producto))
    setProductosDisp(prods.data || [])
    setLoading(false)
  }

  async function handleSave() {
    if (!modal) return
    const cantidad = Number(modal.cantidad)
    if (!modal.productoId || !cantidad || cantidad <= 0) {
      alert('Elegí producto y cantidad mayor a 0')
      return
    }
    try {
      await upsertProductoEnKit(modal.productoId, codigo, cantidad, !!modal.esRegalo)
      setModal(null)
      await load()
    } catch (err) {
      alert('Error al guardar: ' + (err.message || err))
    }
  }

  async function handleRemove(productoId, sku) {
    if (!confirm(`¿Quitar ${sku} de este kit?`)) return
    try {
      await quitarProductoDeKit(productoId, codigo)
      await load()
    } catch (err) {
      alert('Error al quitar: ' + (err.message || err))
    }
  }

  // Separar items en productos pagos (los que el cliente paga) y regalos (no se cobran)
  const itemsPagos   = items.filter(i => !i.es_regalo)
  const itemsRegalos = items.filter(i =>  i.es_regalo)

  // sumCostoSIva: costo s/IVA (lo que figura en productos.costo_unitario)
  // sumCostoCIva: aplica el iva_pct específico de cada producto (10 o 22) — usado para margen real
  const sumCostoSIva = arr => arr.reduce((s, i) => s + Number(i.producto?.costo_unitario || 0) * Number(i.cantidad), 0)
  const sumCostoCIva = arr => arr.reduce((s, i) => {
    const cu  = Number(i.producto?.costo_unitario || 0)
    const iva = Number(i.producto?.iva_pct ?? 22)
    return s + cu * (1 + iva / 100) * Number(i.cantidad)
  }, 0)

  const costoPagos       = sumCostoSIva(itemsPagos)
  const costoRegalos     = sumCostoSIva(itemsRegalos)
  const costoTotal       = costoPagos + costoRegalos
  const costoTotalCIva   = sumCostoCIva(itemsPagos) + sumCostoCIva(itemsRegalos)

  const productosNoAsignados = productosDisp.filter(p => !items.some(i => i.producto?.id === p.id))

  if (loading) return <AdminLayout><div className="page"><div className="card">Cargando…</div></div></AdminLayout>
  if (!kit) return <AdminLayout><div className="page"><div className="card">Kit no encontrado.</div></div></AdminLayout>

  // Margen real: precio público (c/IVA) menos costo c/IVA usando la tasa específica de cada producto
  const margenSusc = Number(kit.precio_susc || 0) - costoTotalCIva
  const margenPct = kit.precio_susc > 0 ? (margenSusc / Number(kit.precio_susc)) * 100 : 0

  return (
    <AdminLayout>
      <div className="page">
        <Link to="/admin/kits" style={{ color: '#64748b', textDecoration: 'none', fontSize: 13 }}>← Volver a kits</Link>
        <h1 style={{ marginTop: 8 }}>{kit.nombre}</h1>
        <p className="lead">{kit.personas} · {TIER_LABEL[kit.tier] || kit.tier}</p>

        {/* Resumen */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12, marginBottom: 22 }}>
          <Stat label="Precio susc/mes" value={`$${Number(kit.precio_susc || 0).toLocaleString('es-UY')}`} />
          <Stat label="Precio compra única" value={`$${Number(kit.precio_unica || 0).toLocaleString('es-UY')}`} />
          <Stat label="Costo c/IVA" value={`$${Math.round(costoTotalCIva).toLocaleString('es-UY')}`}
                sub={costoRegalos > 0
                  ? `${itemsPagos.length} pagos + ${itemsRegalos.length} regalo${itemsRegalos.length !== 1 ? 's' : ''}`
                  : `${items.length} ítems · s/IVA $${Math.round(costoTotal).toLocaleString('es-UY')}`} />
          <Stat label="Margen susc" value={`${margenPct.toFixed(0)}%`}
            color={margenPct > 50 ? '#059669' : margenPct > 35 ? '#D97706' : '#DC2626'}
            sub={costoRegalos > 0 ? `tras regalos` : null} />
        </div>

        {/* Sección 1 — productos del kit (lo que paga el cliente) */}
        <KitSeccion
          titulo={`📦 Productos del kit (${itemsPagos.length})`}
          esRegalo={false}
          items={itemsPagos}
          totalCosto={costoPagos}
          onAdd={() => setModal({ mode: 'add', productoId: '', cantidad: 1, esRegalo: false })}
          onEdit={(p, cant, regalo) => setModal({ mode: 'edit', productoId: p.id, productoLabel: `${p.sku} ${p.nombre}`, cantidad: cant, esRegalo: regalo })}
          onRemove={handleRemove}
        />

        {/* Sección 2 — regalos del kit (no se cobran) */}
        <div style={{ height: 24 }}></div>
        <KitSeccion
          titulo={`🎁 Regalos del kit (${itemsRegalos.length})`}
          subtitulo="Estos productos se incluyen en cada entrega como cortesía. Bajan stock pero no se cobran al cliente."
          esRegalo={true}
          items={itemsRegalos}
          totalCosto={costoRegalos}
          onAdd={() => setModal({ mode: 'add', productoId: '', cantidad: 1, esRegalo: true })}
          onEdit={(p, cant, regalo) => setModal({ mode: 'edit', productoId: p.id, productoLabel: `${p.sku} ${p.nombre}`, cantidad: cant, esRegalo: regalo })}
          onRemove={handleRemove}
        />

        {/* Modal: agregar / editar */}
        {modal && (
          <div
            onClick={() => setModal(null)}
            style={{
              position: 'fixed', inset: 0, background: 'rgba(15,31,61,.55)', zIndex: 1000,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
            }}
          >
            <div
              onClick={e => e.stopPropagation()}
              style={{ background: '#fff', borderRadius: 14, padding: 22, maxWidth: 520, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,.25)' }}
            >
              <h3 style={{ margin: '0 0 4px', fontSize: 19, fontWeight: 800, color: '#0F1F3D' }}>
                {modal.mode === 'edit'
                  ? `Editar — ${modal.productoLabel}`
                  : (modal.esRegalo ? `Agregar regalo a ${kit.nombre}` : `Agregar producto a ${kit.nombre}`)}
              </h3>
              <p style={{ margin: '0 0 16px', fontSize: 13, color: '#64748b' }}>
                {modal.mode === 'edit'
                  ? 'Cuántas unidades del producto lleva este kit.'
                  : (modal.esRegalo
                      ? 'Elegí un producto del catálogo. Va incluido en el kit como regalo (no se cobra).'
                      : 'Elegí un producto del catálogo y la cantidad por kit.')}
              </p>

              {modal.mode === 'add' && (
                <label style={{ display: 'block', marginBottom: 14 }}>
                  <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Producto</span>
                  <select
                    value={modal.productoId}
                    onChange={e => setModal({ ...modal, productoId: e.target.value })}
                    style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                  >
                    <option value="">— Elegí un producto —</option>
                    {productosNoAsignados.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.sku} · {p.nombre}{p.presentacion ? ` (${p.presentacion})` : ''} · stock {Number(p.stock_actual || 0)}
                      </option>
                    ))}
                  </select>
                  {productosNoAsignados.length === 0 && (
                    <div style={{ fontSize: 12, color: '#92400E', marginTop: 6 }}>
                      Todos los productos activos ya están en este kit.
                    </div>
                  )}
                </label>
              )}

              <label style={{ display: 'block', marginBottom: 16 }}>
                <span style={{ display: 'block', fontSize: 12, fontWeight: 700, color: '#475569', marginBottom: 4 }}>Cantidad</span>
                <input
                  type="number" min="1" step="1"
                  value={modal.cantidad}
                  onChange={e => setModal({ ...modal, cantidad: e.target.value })}
                  autoFocus
                  style={{ width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14 }}
                />
                <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>
                  En unidades reales del producto (botellas / rollos / unidades).
                </div>
              </label>

              {/* Toggle es_regalo (visible solo al editar — al agregar se decide por la sección) */}
              {modal.mode === 'edit' && (
                <label style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px',
                  background: modal.esRegalo ? '#FEF3C7' : '#F8FAFC',
                  border: `1px solid ${modal.esRegalo ? '#FCD34D' : '#E2E8F0'}`,
                  borderRadius: 8, marginBottom: 16, cursor: 'pointer',
                }}>
                  <input
                    type="checkbox"
                    checked={!!modal.esRegalo}
                    onChange={e => setModal({ ...modal, esRegalo: e.target.checked })}
                    style={{ width: 18, height: 18, cursor: 'pointer' }}
                  />
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: '#92400E' }}>🎁 Es regalo del kit</div>
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>
                      Si está activo, el cliente lo recibe pero no lo paga (afecta costo, no precio).
                    </div>
                  </div>
                </label>
              )}

              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <button onClick={() => setModal(null)} style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Cancelar</button>
                <button onClick={handleSave} style={{ background: '#0F1F3D', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>Guardar</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '12px 14px' }}>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '.04em' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: color || '#0F1F3D', marginTop: 2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

/* Sección reusable de items del kit (productos pagos o regalos).
 * `esRegalo` define el color/tono y el copy del botón de agregar.
 * Empty state distinto entre pagos (warning) y regalos (info, opcional). */
function KitSeccion({ titulo, subtitulo, esRegalo, items, totalCosto, onAdd, onEdit, onRemove }) {
  const accent      = esRegalo ? '#92400E' : '#0F1F3D'
  const accentBg    = esRegalo ? '#FEF3C7' : '#0F1F3D'
  const accentText  = esRegalo ? '#92400E' : '#fff'
  const labelAdd    = esRegalo ? '+ Agregar regalo' : '+ Agregar producto'
  const labelTotal  = esRegalo ? 'COSTO REGALOS s/IVA (subtotal)' : 'COSTO PRODUCTOS s/IVA (subtotal)'
  const emptyMsg    = esRegalo
    ? 'Este kit no tiene regalos. Agregá productos cortesía para sorprender al cliente.'
    : 'Este kit todavía no tiene productos.'
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
        <h2 className="section-head" style={{ margin: 0, color: accent }}>{titulo}</h2>
        <button
          onClick={onAdd}
          style={{ background: accentBg, color: accentText, padding: '8px 14px', borderRadius: 8, border: esRegalo ? '1px solid #FCD34D' : 'none', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
        >{labelAdd}</button>
      </div>
      {subtitulo && <p style={{ margin: '0 0 10px', fontSize: 12, color: '#64748b' }}>{subtitulo}</p>}

      {items.length === 0 ? (
        <div className="card" style={{ color: 'var(--muted)', fontSize: 13 }}>{emptyMsg}</div>
      ) : (
        <table className="t-table">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Producto</th>
              <th>Pres.</th>
              <th style={{ textAlign: 'right' }}>Costo u.</th>
              <th style={{ textAlign: 'right' }}>Stock</th>
              <th style={{ textAlign: 'right' }}>Cantidad</th>
              <th style={{ textAlign: 'right' }}>Subtotal</th>
              <th style={{ textAlign: 'right', width: 160 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.map(i => {
              const p = i.producto
              const subtotal = Number(p.costo_unitario || 0) * Number(i.cantidad)
              const stockOK = Number(p.stock_actual || 0) >= Number(i.cantidad)
              return (
                <tr key={p.id}>
                  <td><Link to={`/admin/stock/${p.sku}`} style={{ color: '#0F1F3D', fontWeight: 700, textDecoration: 'none' }}>{p.sku}</Link></td>
                  <td>
                    {esRegalo && <span style={{ marginRight: 6 }}>🎁</span>}
                    <strong>{p.nombre}</strong>
                    {p.marca && <span style={{ color: '#94a3b8', fontSize: 12, marginLeft: 6 }}>· {p.marca}</span>}
                    {p.grupo_equivalencia && (
                      <span style={{
                        marginLeft: 6, fontSize: 10, fontWeight: 700, color: '#7C3AED',
                        background: '#F3E8FF', padding: '2px 6px', borderRadius: 999,
                      }}>🔗 {p.grupo_equivalencia}</span>
                    )}
                  </td>
                  <td style={{ fontSize: 12, color: '#64748b' }}>{p.presentacion || '—'}</td>
                  <td style={{ textAlign: 'right' }}>${Number(p.costo_unitario || 0).toFixed(2)}</td>
                  <td style={{ textAlign: 'right', color: stockOK ? '#059669' : '#DC2626', fontWeight: 700 }}>
                    {Number(p.stock_actual || 0)}
                  </td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>×{Number(i.cantidad)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 700 }}>${subtotal.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>
                    <button
                      onClick={() => onEdit(p, Number(i.cantidad), !!i.es_regalo)}
                      style={{ background: 'transparent', border: '1px solid #cbd5e1', borderRadius: 6, padding: '4px 8px', marginRight: 6, cursor: 'pointer', fontSize: 12 }}
                    >✏️ Editar</button>
                    <button
                      onClick={() => onRemove(p.id, p.sku)}
                      style={{ background: '#FEF2F2', border: '1px solid #FCA5A5', color: '#7F1D1D', borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 12 }}
                    >🗑️ Quitar</button>
                  </td>
                </tr>
              )
            })}
            <tr style={{ background: '#f8fafc', fontWeight: 800 }}>
              <td colSpan={6} style={{ textAlign: 'right' }}>{labelTotal}</td>
              <td style={{ textAlign: 'right' }}>${totalCosto.toFixed(2)}</td>
              <td></td>
            </tr>
          </tbody>
        </table>
      )}
    </>
  )
}
